import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col flex-1 min-h-0 rounded-xl bg-white dark:bg-(--colors-background-black-default) shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] dark:shadow-[0px_0px_4px_rgba(255,255,255,0.1)] overflow-hidden" };
const _hoisted_2 = { class: "flex items-center justify-between gap-3 px-5 h-14 border-b border-gainsboro dark:border-(--colors-border-base)" };
const _hoisted_3 = { class: "flex items-center gap-2" };
const _hoisted_4 = { class: "text-base font-semibold text-(--colors-text-default)" };
const _hoisted_5 = { class: "flex-1 overflow-x-hidden min-h-0" };
const _hoisted_6 = { class: "border-t border-gainsboro dark:border-(--colors-border-base) px-5 py-3 bg-white dark:bg-(--colors-background-black-default)" };
import { computed, h, onMounted, provide, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import { Badge, Button, DataTable, FacetedSearchDrawer, Pager, TableControls, Tooltip, useTableQuery, DEFAULT_TABLE_LABELS, TABLE_QUERY_KEY, TABLE_PERMISSIONS_KEY, TABLE_LABELS_KEY, } from "@eudonet/opal";
import { usePermissions } from "@/shared/composables/usePermissions";
import SubscriptionCreateModal from "@/modules/finance/presentation/components/SubscriptionCreateModal.vue";
import ModuleState from "@/shared/components/ModuleState.vue";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import { useListSettings } from "@/shared/composables/useListSettings";
import { useAsyncData } from "@/shared/composables/useAsyncData";
import { formatDate as formatDateWithProfile } from "@/shared/utils/date-formatting";
import { useProfileStore } from "@/modules/settings/public";
import { getSubscriptions } from "@/modules/finance/infrastructure/adapters/subscriptionService";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { matchesSearchTerm } from "@/shared/utils/tableSearch";
import { subscriptionStatusTheme, subscriptionStatusIcon } from "@/modules/finance/presentation/composables/subscriptionStatusBadge";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactSubscriptionsTab',
    props: {
        contactId: {},
        contactName: {}
    },
    setup(__props) {
        const props = __props;
        const { t, locale } = useI18n();
        const { canEditOphub: canEdit, disabledTooltip } = usePermissions();
        const { formatAmount } = useFinanceFormatters();
        const profileStore = useProfileStore();
        // R30 — dates follow the profile dateFormat like every other date on the contact
        // fiche (ContactPreviewDrawer), not the finance locale-only Intl formatter.
        const formatDate = (date) => formatDateWithProfile(date, locale.value, profileStore.profile?.dateFormat);
        const ROW_OPTIONS = [10, 30, 50];
        // ── Table query (search, pagination) ─────────────────────────────────────────
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0] });
        // Provide the table context the OPAL sub-components inject (same contract as TableShell).
        provide(TABLE_QUERY_KEY, tableQuery);
        const tablePermissions = { canSaveView: false, canShareView: false };
        provide(TABLE_PERMISSIONS_KEY, tablePermissions);
        const labelsComputed = computed(() => ({
            ...DEFAULT_TABLE_LABELS,
            filterButton: t("table.filter"),
            eraseAll: t("table.eraseAll"),
            hideFilters: (n) => t("table.hideFilters", { count: n }),
            showFilters: (n) => t("table.showFilters", { count: n }),
            closeSearch: t("table.closeSearch"),
            removeFilter: (label) => t("table.removeFilter", { label }),
        }));
        // Cast: opal's dist types brand ComputedRef against its own bundled Vue copy; the
        // runtime value is a valid ref the sub-components unwrap. Same reason list pages
        // pass labels through TableShell rather than providing the key directly.
        provide(TABLE_LABELS_KEY, labelsComputed);
        // Divergence from DonationTabHistory: NO useTableQueryUrlSync here — the contact
        // fiche hosts two such tabs (donations + subscriptions), so syncing ?page/?q for
        // both would make them clash on the same URL params.
        // ── Server pagination (search stays client-side, see filteredSubscriptions) ───
        const { data: result, loading, loadError, load, reload } = useAsyncData(() => getSubscriptions({
            contactId: props.contactId,
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
        }));
        watch(() => [tableQuery.state.page, tableQuery.state.pageSize], load);
        watch(() => props.contactId, () => {
            if (tableQuery.state.page === 1)
                load();
            else
                tableQuery.setPage(1); // page watcher triggers the load
        });
        onMounted(load);
        // ── Add subscription modal ────────────────────────────────────────────────────
        const showCreateModal = ref(false);
        const createModalMounted = ref(false);
        const lockedDonor = computed(() => ({
            donorType: "Individual",
            contactId: props.contactId,
            displayName: props.contactName ?? "",
        }));
        function openCreateModal() {
            createModalMounted.value = true;
            showCreateModal.value = true;
        }
        function onSubscriptionCreated() {
            load(); // refetch current page; the modal owns its own success toast
        }
        const totalItems = computed(() => result.value?.totalItems ?? 0);
        const totalPages = computed(() => result.value?.totalPages ?? 0);
        // useAsyncData wraps the result in DeepReadonly — spread into a fresh mutable
        // array for DataTable's `data: TData[]` prop.
        const subscriptions = computed(() => [...(result.value?.result ?? [])]);
        // ── Client-side search ────────────────────────────────────────────────────────
        // Filters the subscriptions of the currently loaded page against the search term.
        // Matches the visible cell content (translated frequency/status, formatted
        // amount/next due date) so the user can search exactly what they see. No request
        // is made — this is purely in-memory (mirrors DonationListView).
        const searchActive = computed(() => !!tableQuery.state.search?.trim());
        const filteredSubscriptions = computed(() => {
            const term = tableQuery.state.search ?? "";
            if (!term.trim())
                return subscriptions.value;
            return subscriptions.value.filter((s) => matchesSearchTerm([
                s.frequency ? t(`finance.subscriptions.frequency.${s.frequency}`) : "",
                formatAmount(s.amount, s.currency ?? "EUR"),
                s.status ? t(`finance.subscriptions.status.${s.status}`) : "",
                formatDate(s.nextDueDate),
            ], term));
        });
        // When a search is active the table shows the filtered subset of the current page,
        // so the pager must reflect that subset (one page, filtered count) instead of the
        // server-side totals. With no search term the server pagination drives the pager.
        const pagerTotalItems = computed(() => searchActive.value ? filteredSubscriptions.value.length : totalItems.value);
        const pagerTotalPages = computed(() => (searchActive.value ? 1 : totalPages.value));
        const pagerCurrentPage = computed(() => (searchActive.value ? 1 : tableQuery.state.page));
        // ── Column settings (gear → FacetedSearchDrawer) ─────────────────────────────
        const settingsColumns = () => [
            {
                key: "frequency",
                label: t("contact.detail.subscriptions.columns.frequency"),
                visible: true,
                pinned: false,
            },
            {
                key: "amount",
                label: t("contact.detail.subscriptions.columns.amount"),
                visible: true,
                pinned: false,
            },
            {
                key: "status",
                label: t("contact.detail.subscriptions.columns.status"),
                visible: true,
                pinned: false,
            },
            {
                key: "nextDueDate",
                label: t("contact.detail.subscriptions.columns.nextPayment"),
                visible: true,
                pinned: false,
            },
        ];
        const columnSettings = useListSettings({
            entity: "contact-subscriptions",
            columns: settingsColumns,
            defaultOrder: ["frequency", "amount", "status", "nextDueDate"],
            defaultPinning: { left: [], right: [] },
        });
        const columnOrder = computed(() => [...columnSettings.order.value]);
        // ── Columns ──────────────────────────────────────────────────────────────────
        // Same rendering bricks as SubscriptionListView: icon-based status Badge (shared
        // theme + icon maps), frequency label, formatted amount/date, "–" fallbacks.
        const columns = computed(() => [
            {
                key: "frequency",
                header: t("contact.detail.subscriptions.columns.frequency"),
                draggable: true,
                cell: ({ row }) => {
                    const freq = row.getValue("frequency");
                    const label = freq ? t(`finance.subscriptions.frequency.${freq}`) : "–";
                    return h(RouterLink, {
                        to: `/subscriptions/${row.original.id}`,
                        class: "text-blue-500 hover:underline",
                        "data-testid": `contact-subscription-link-${row.original.id}`,
                    }, () => label);
                },
            },
            {
                key: "amount",
                header: t("contact.detail.subscriptions.columns.amount"),
                draggable: true,
                cell: ({ row }) => formatAmount(row.getValue("amount"), row.original.currency ?? "EUR"),
            },
            {
                key: "status",
                header: t("contact.detail.subscriptions.columns.status"),
                draggable: true,
                cell: ({ row }) => {
                    const status = row.getValue("status");
                    return h(Badge, {
                        label: t(`finance.subscriptions.status.${status}`),
                        theme: subscriptionStatusTheme[status],
                        showCloseIcon: false,
                        size: "sm",
                        leftIcon: subscriptionStatusIcon[status],
                    });
                },
            },
            {
                key: "nextDueDate",
                header: t("contact.detail.subscriptions.columns.nextPayment"),
                draggable: true,
                cell: ({ row }) => formatDate(row.original.nextDueDate),
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _cache[2] || (_cache[2] = _createElementVNode("span", { class: "ri-loop-left-line text-lg text-(--colors-text-secondary)" }, null, -1 /* CACHED */)),
                            _createElementVNode("h2", _hoisted_4, _toDisplayString(_unref(t)("contact.detail.subscriptions.title")), 1 /* TEXT */)
                        ]),
                        _createVNode(_unref(Tooltip), {
                            text: _unref(disabledTooltip),
                            disabled: _unref(canEdit),
                            tabindex: _unref(canEdit) ? undefined : 0
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_unref(Button), {
                                    variant: "primary",
                                    size: "sm",
                                    "icon-left": "ri-add-line",
                                    label: _unref(t)('contact.detail.subscriptions.addSubscription'),
                                    disabled: !_unref(canEdit),
                                    "data-testid": "contact-subscription-add",
                                    onClick: openCreateModal
                                }, null, 8 /* PROPS */, ["label", "disabled"])
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["text", "disabled", "tabindex"])
                    ]),
                    (_unref(loading))
                        ? (_openBlock(), _createBlock(ModuleLoading, { key: 0 }))
                        : (_unref(loadError))
                            ? (_openBlock(), _createBlock(ModuleState, {
                                key: 1,
                                type: "error",
                                class: "flex-1",
                                title: _unref(t)('contact.detail.subscriptions.loadError.title'),
                                "cta-icon": "ri-refresh-line",
                                "cta-label": _unref(t)('contact.detail.subscriptions.loadError.retry'),
                                onCta: _unref(reload)
                            }, null, 8 /* PROPS */, ["title", "cta-label", "onCta"]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                _createVNode(_unref(TableControls), { "show-filter": false }),
                                _createElementVNode("div", _hoisted_5, [
                                    _createVNode(_unref(DataTable), {
                                        columns: columns.value,
                                        data: filteredSubscriptions.value,
                                        "column-visibility": _unref(columnSettings).visibility,
                                        "column-order": columnOrder.value,
                                        "column-pinning": _unref(columnSettings).pinning.value,
                                        "column-sizing": _unref(columnSettings).widths.value,
                                        "has-settings": "",
                                        "sticky-header": "",
                                        "empty-text": _unref(t)('contact.detail.subscriptions.empty'),
                                        "settings-aria-label": _unref(t)('columnSettings.title'),
                                        onSettingsClick: _unref(columnSettings).openDrawer,
                                        "onUpdate:columnOrder": _unref(columnSettings).setOrder,
                                        "onUpdate:columnPinning": _unref(columnSettings).setPinning,
                                        "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                                    }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "column-sizing", "empty-text", "settings-aria-label", "onSettingsClick", "onUpdate:columnOrder", "onUpdate:columnPinning", "onUpdate:columnSizing"])
                                ]),
                                _createElementVNode("div", _hoisted_6, [
                                    _createVNode(_unref(Pager), {
                                        "current-page": pagerCurrentPage.value,
                                        "total-pages": pagerTotalPages.value,
                                        "total-items": pagerTotalItems.value,
                                        "items-per-page": _unref(tableQuery).state.pageSize,
                                        "rows-per-page-options": ROW_OPTIONS,
                                        "rows-per-page-label": _unref(t)('pagination.rowsPerPage'),
                                        "items-count-separator": _unref(t)('pagination.of'),
                                        "previous-label": _unref(t)('pagination.previous'),
                                        "next-label": _unref(t)('pagination.next'),
                                        "go-to-page-label": _unref(t)('pagination.goToPage'),
                                        "go-label": _unref(t)('pagination.go'),
                                        "show-go-to-page": "",
                                        onPageChange: _unref(tableQuery).setPage,
                                        onItemsPerPageChange: _unref(tableQuery).setPageSize
                                    }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                                ])
                            ], 64 /* STABLE_FRAGMENT */))
                ]),
                _createVNode(_unref(FacetedSearchDrawer), {
                    modelValue: _unref(columnSettings).drawerOpen.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(columnSettings).drawerOpen.value) = $event)),
                    columns: _unref(columnSettings).settingsItems.value,
                    "default-columns": _unref(columnSettings).defaultSettingsItems.value,
                    "column-order": _unref(columnSettings).order.value,
                    labels: _unref(columnSettings).labels.value,
                    onSave: _unref(columnSettings).handleSave,
                    onReset: _unref(columnSettings).handleReset
                }, null, 8 /* PROPS */, ["modelValue", "columns", "default-columns", "column-order", "labels", "onSave", "onReset"]),
                (createModalMounted.value)
                    ? (_openBlock(), _createBlock(SubscriptionCreateModal, {
                        key: 0,
                        modelValue: showCreateModal.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showCreateModal).value = $event)),
                        "locked-donor": lockedDonor.value,
                        onCreated: onSubscriptionCreated
                    }, null, 8 /* PROPS */, ["modelValue", "locked-donor"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
